import React, { ChangeEvent, useEffect, useState } from 'react';
import { Box, BoxProps, Flex, Input, Text } from '@chakra-ui/react';
import { useDebouncedCallback } from 'use-debounce';
import {
  AMOUNT_EQUAL_MESSAGE,
  AMOUNT_MAX_VALUE_MESSAGE,
  AMOUNT_RANGE_MESSAGE,
  INVALID_AMOUNT_MESSAGE,
  isAmountAboveMax,
  isAmountInputAllowed,
  isAmountRangeEqual,
  isAmountRangeInverted,
  isAmountRangeValid,
  isValidPositiveAmount,
} from '../../../utils/amountValidation';

export interface AmountRange {
  amountStart?: string;
  amountEnd?: string;
}

interface AmountRangeFilterProps extends AmountRange, Omit<BoxProps, 'onChange'> {
  onChange: (range: AmountRange) => void;
  debounceDelay?: number;
}

type AmountField = keyof AmountRange;

const ERROR_ID = 'amount-range-error';

const toParam = (value: string): string | undefined => value === '' ? undefined : value;

export const AmountRangeFilter = ({ amountStart, amountEnd, onChange, debounceDelay = 500, ...boxProps }: AmountRangeFilterProps) => {
  const [range, setRange] = useState({ amountStart: amountStart ?? '', amountEnd: amountEnd ?? '' });
  const [focusedField, setFocusedField] = useState<AmountField | null>(null);

  useEffect(() => {
    setRange({ amountStart: amountStart ?? '', amountEnd: amountEnd ?? '' });
  }, [amountStart, amountEnd]);

  const debouncedChange = useDebouncedCallback((next: AmountRange) => onChange(next), debounceDelay);

  const handleChange = (field: AmountField) => (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (!isAmountInputAllowed(value)) {
      return;
    }
    const next = { ...range, [field]: value };
    setRange(next);

    if (isAmountRangeValid(next.amountStart, next.amountEnd)) {
      debouncedChange({ amountStart: toParam(next.amountStart), amountEnd: toParam(next.amountEnd) });
    } else {
      debouncedChange.cancel();
    }
  };

  // Mientras el campo tiene foco no se marca un valor incompleto (p. ej. "12.") como error.
  const hasInvalidFormat = (field: AmountField): boolean =>
    range[field] !== '' && !isValidPositiveAmount(range[field]) && focusedField !== field;

  const startInvalid = hasInvalidFormat('amountStart');
  const endInvalid = hasInvalidFormat('amountEnd');
  const startAboveMax = isAmountAboveMax(range.amountStart);
  const endAboveMax = isAmountAboveMax(range.amountEnd);
  const isInverted = isAmountRangeInverted(range.amountStart, range.amountEnd);
  const isEqual = isAmountRangeEqual(range.amountStart, range.amountEnd);
  const isRangeInvalid = isInverted || isEqual;

  const getErrorMessage = (): string | null => {
    if (startInvalid || endInvalid) return INVALID_AMOUNT_MESSAGE;
    if (startAboveMax || endAboveMax) return AMOUNT_MAX_VALUE_MESSAGE;
    if (isInverted) return AMOUNT_RANGE_MESSAGE;
    if (isEqual) return AMOUNT_EQUAL_MESSAGE;
    return null;
  };
  const errorMessage = getErrorMessage();

  const inputProps = (field: AmountField, label: string, isInvalid: boolean) => ({
    value: range[field],
    onChange: handleChange(field),
    onFocus: () => setFocusedField(field),
    onBlur: () => setFocusedField(null),
    placeholder: label,
    'aria-label': label,
    isInvalid,
    'aria-describedby': isInvalid ? ERROR_ID : undefined,
    type: 'text',
    inputMode: 'decimal' as const,
    autoComplete: 'off',
    width: '12em',
  });

  return (
    <Box {...boxProps}>
      <Flex alignItems="center" gap="s">
        <Input {...inputProps('amountStart', 'Monto mínimo', startInvalid || startAboveMax || isRangeInvalid)} />
        <Input {...inputProps('amountEnd', 'Monto máximo', endInvalid || endAboveMax || isRangeInvalid)} />
      </Flex>
      {errorMessage &&
        <Text id={ERROR_ID} role="alert" color="destructive.700" fontSize="s" marginTop="s" maxWidth="24em">
          {errorMessage}
        </Text>
      }
    </Box>
  );
};
